/**
 * FWD 弯沉盆指标的 Strong / Moderate / Weak 评级阈值（唯一前端出口）。
 *
 * 权威源在后端（pms-go-api internal/fwd/rating.go），经 GET /fwd-raw/rating-config 下发。
 * 与 decayConfig / gradeConfig 同一套做法：前端不各处硬编码，一律从这里读，避免前后端漂移。
 *
 * 默认值 = 后端当前值，作为接口未就绪/失败时的兜底；应用启动时 loadFwdRatingConfig() 用后端值覆盖。
 * 消费方在「调用时」读 fwdRatingConfig，故后端值加载完成后自动生效，无需重新挂载。
 */
import { fwdRawApi } from '@/api/fwdRaw'
import type { FwdRatingBand } from '@/types/fwdRaw'

export type FwdRatingLevel = 'strong' | 'moderate' | 'weak'

/**
 * 可变单例：dataIndex → 阈值。键就是表格列的 dataIndex，取用时直接查表。
 * LLI2(d6) 不在表里 —— 源表没给它评级标准，不着色。
 */
export const fwdRatingConfig: Record<string, FwdRatingBand> = {
  d1: { metric: 'd1', moderate: 445, weak: 570, higher_is_worse: true },
  sci300: { metric: 'sci300', moderate: 190, weak: 275, higher_is_worse: true },
  bdi: { metric: 'bdi', moderate: 135, weak: 175, higher_is_worse: true },
  bci: { metric: 'bci', moderate: 50, weak: 95, higher_is_worse: true },
  // RoC 唯一反向：越小越差（Strong ≥175、Weak ≤105）
  basin_ratio: { metric: 'basin_ratio', moderate: 175, weak: 105, higher_is_worse: false },
}

let loaded = false

// 从后端拉取并覆盖本地默认值；失败时静默沿用默认值（与后端一致），不阻塞渲染。
// 幂等：多次调用只真正请求一次。
export async function loadFwdRatingConfig(): Promise<void> {
  if (loaded) return
  try {
    const cfg = await fwdRawApi.getRatingConfig()
    if (cfg && Array.isArray(cfg.bands) && cfg.bands.length > 0) {
      cfg.bands.forEach((band) => {
        if (band?.metric && Number.isFinite(band.moderate) && Number.isFinite(band.weak)) {
          fwdRatingConfig[band.metric] = band
        }
      })
      loaded = true
    }
  } catch {
    // 保底：沿用默认值（与后端一致）。下次调用可再试。
  }
}

/**
 * 判定单个值的评级。表里没有该列、或值为空时返回 null（不着色）。
 *
 * 边界值归更差的一侧：445 是 Moderate 不是 Strong，与后端及决策树口径一致
 * （车辙 15mm 归深辙侧、LLI 95 归超限侧）。
 *
 * higher_is_worse=false（RoC）时整个比较方向翻转 —— 这是本文件唯一容易写错的地方：
 * 用同一个 >= 套五列，RoC 会被染成完全相反的颜色，且看着毫无破绽。
 */
export function rateFwdValue(dataIndex: string, value: number | null | undefined): FwdRatingLevel | null {
  const band = fwdRatingConfig[dataIndex]
  if (!band || value == null || !Number.isFinite(value)) return null

  if (band.higher_is_worse) {
    if (value >= band.weak) return 'weak'
    if (value >= band.moderate) return 'moderate'
    return 'strong'
  }
  if (value <= band.weak) return 'weak'
  if (value <= band.moderate) return 'moderate'
  return 'strong'
}

/** 悬停提示：给出等级与判据，免得只能靠记颜色。 */
export function describeFwdRating(dataIndex: string, level: FwdRatingLevel): string {
  const band = fwdRatingConfig[dataIndex]
  if (!band) return ''
  const label = { strong: 'Strong', moderate: 'Moderate', weak: 'Weak' }[level]
  const gte = band.higher_is_worse
  if (level === 'weak') return `${label}（${gte ? '≥' : '≤'} ${band.weak}）`
  if (level === 'strong') return `${label}（${gte ? '<' : '>'} ${band.moderate}）`
  const lo = gte ? band.moderate : band.weak
  const hi = gte ? band.weak : band.moderate
  return `${label}（${lo} ~ ${hi}）`
}
